import { applyAuthHeaders, resolveRequestOrgId } from '@/api/authHeaders'
import { resolveUnauthorized } from '@/api/unauthorized'
import { env } from '@/config/env'
import i18n, { SUPPORTED_LANGS, type SupportedLang } from '@/i18n'

/**
 * 统一 HTTP 客户端。
 * 本地：`env.apiBaseUrl=/api`（Vite proxy → `VITE_API_BASE_URL`）；
 * 构建：直连 `VITE_API_BASE_URL`。
 * 鉴权：正式 JWT 优先；没有时回落 `.env.development.local` 的
 * `VITE_DEV_AUTHORIZATION` / `VITE_DEV_ORG_ID`。
 * 语言：`X-Accept-Language` 取当前页面语言（`zh-CN` / `en-US`）。
 * 401：浏览器踢登录；客户端续宿主 JWT 后重试原请求 1 次（对齐 org-console）。
 */

export interface ApiResponse<T> {
  code: number
  message?: string
  data: T
}

export class ApiError extends Error {
  readonly code: number
  readonly status?: number
  readonly data?: unknown

  constructor(code: number, message: string, status?: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.data = data
  }
}

type JsonBody = Record<string, unknown> | unknown[] | null

export type UploadProgress = {
  loaded: number
  total: number
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  /** 普通对象会 JSON.stringify；FormData / Blob 原样上传且不强制 Content-Type */
  body?: BodyInit | JsonBody
  /** 追加到 path 的 query（undefined/null 会跳过） */
  params?: Record<string, string | number | boolean | null | undefined>
  /** 换码等匿名接口：不附带 Authorization / x-org-id */
  skipAuth?: boolean
  /** 覆盖默认 X-Org-Id（orgClient 用） */
  orgId?: string | null
  /**
   * 上传进度（已发送字节）。传入时走 XMLHttpRequest，
   * 因为 fetch 拿不到 request body 发送进度。
   */
  onUploadProgress?: (progress: UploadProgress) => void
}

function isPlainBody(body: unknown): body is JsonBody {
  if (body == null) return true
  if (typeof body !== 'object') return false
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return false
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
    return false
  if (ArrayBuffer.isView(body)) return false
  return true
}

function joinUrl(
  base: string,
  path: string,
  params?: RequestOptions['params'],
): string {
  const url = `${base}${path}`
  if (!params) return url

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  if (!qs) return url
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`
}

/** developer-api 业务码「未登录」：HTTP 200 + code，按 401 处理 */
export const DEVELOPER_UNAUTHORIZED_CODE = 32311

async function readPayload<T>(res: Response): Promise<ApiResponse<T> | undefined> {
  try {
    return (await res.json()) as ApiResponse<T>
  } catch {
    return undefined
  }
}

function isUnauthorized(
  res: Response,
  payload: ApiResponse<unknown> | undefined,
  bizUnauthorizedCode?: number,
): boolean {
  if (res.status === 401) return true
  return bizUnauthorizedCode != null && payload?.code === bizUnauthorizedCode
}

function toBizResult<T>(res: Response, payload: ApiResponse<T> | undefined): T {
  if (!payload) {
    throw new ApiError(
      res.status || -1,
      res.ok ? 'Invalid JSON response' : `Request failed: ${res.status}`,
      res.status,
    )
  }

  if (!res.ok) {
    throw new ApiError(
      payload.code ?? res.status,
      payload.message ?? `Request failed: ${res.status}`,
      res.status,
      payload.data,
    )
  }

  if (payload.code !== 0) {
    throw new ApiError(
      payload.code,
      payload.message ?? 'Request failed',
      res.status,
      payload.data,
    )
  }

  return payload.data
}

/** 当前页面语言；不在支持列表时回落到 zh-CN（与 i18n fallback 一致）。 */
function currentPageLanguage(): SupportedLang {
  const lang = i18n.resolvedLanguage ?? i18n.language
  return SUPPORTED_LANGS.includes(lang as SupportedLang)
    ? (lang as SupportedLang)
    : 'zh-CN'
}

function applyLanguageHeader(headers: Headers) {
  if (!headers.has('X-Accept-Language')) {
    headers.set('X-Accept-Language', currentPageLanguage())
  }
}

function buildHeadersAndBody(
  options: RequestOptions,
): { headers: Headers; body: BodyInit | undefined } {
  const headers = new Headers(options.headers)
  applyLanguageHeader(headers)
  if (!options.skipAuth) applyAuthHeaders(headers, options.orgId)

  if (options.body === undefined) {
    return { headers, body: undefined }
  }
  if (isPlainBody(options.body)) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    return { headers, body: JSON.stringify(options.body) }
  }
  return { headers, body: options.body }
}

function isXhrBody(body: BodyInit): body is XMLHttpRequestBodyInit {
  return !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
}

/** XHR 不能发 ReadableStream；上传进度只用于 FormData / Blob 等可计算长度的 body。 */
function toXhrBody(body: BodyInit | undefined): XMLHttpRequestBodyInit | null {
  if (body == null) return null
  if (!isXhrBody(body)) {
    throw new TypeError('Upload progress does not support a streaming request body')
  }
  return body
}

/** 原样发送：不加鉴权头、不拼 baseUrl（直传对象存储也用这个） */
export function sendWithXhr(
  url: string,
  method: string,
  headers: Headers,
  body: BodyInit | undefined,
  signal: AbortSignal | undefined,
  onUploadProgress: (progress: UploadProgress) => void,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open(method, url)
    headers.forEach((value, key) => {
      xhr.setRequestHeader(key, value)
    })

    const report = (loaded: number, total: number) => {
      onUploadProgress({ loaded, total })
    }

    // 每次新开 XHR（含 401 重试）先清零，避免进度只增不减卡在封顶值
    report(0, 0)

    xhr.upload.onprogress = (event) => {
      report(event.loaded, event.lengthComputable ? event.total : 0)
    }
    xhr.upload.onload = () => {
      report(1, 1)
    }

    xhr.onload = () => {
      resolve(
        new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText,
        }),
      )
    }
    xhr.onerror = () => {
      reject(new TypeError('Network request failed'))
    }
    xhr.onabort = () => {
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
    }

    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    xhr.addEventListener('loadend', () => {
      signal?.removeEventListener('abort', onAbort)
    })

    xhr.send(toXhrBody(body))
  })
}

/**
 * 开发环境 StrictMode 会把加载 effect 立刻卸掉再挂上，同一 GET 会打两次。
 * 相同地址还在飞时共用这一次；结束后再请求仍会重新发出。
 */
const inflightGets = new Map<string, Promise<unknown>>()

function shareInflightGet<T>(key: string, load: () => Promise<T>): Promise<T> {
  const existing = inflightGets.get(key)
  if (existing) return existing as Promise<T>
  const pending = load().finally(() => {
    if (inflightGets.get(key) === pending) inflightGets.delete(key)
  })
  inflightGets.set(key, pending)
  return pending
}

async function executeRequest<T>(
  baseUrl: string,
  path: string,
  options: RequestOptions = {},
  bizUnauthorizedCode?: number,
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const shareable =
    method === 'GET' &&
    options.body == null &&
    !options.onUploadProgress &&
    !options.signal
  if (!shareable) {
    return performRequest(baseUrl, path, options, bizUnauthorizedCode)
  }
  const orgKey = options.skipAuth ? 'anon' : resolveRequestOrgId(options.orgId)
  const language = new Headers(options.headers).get('X-Accept-Language') || currentPageLanguage()
  const key = `${method} ${orgKey} ${language} ${joinUrl(baseUrl, path, options.params)}`
  return shareInflightGet(key, () =>
    performRequest(baseUrl, path, options, bizUnauthorizedCode),
  )
}

async function performRequest<T>(
  baseUrl: string,
  path: string,
  options: RequestOptions = {},
  bizUnauthorizedCode?: number,
): Promise<T> {
  const {
    params,
    body,
    headers: initHeaders,
    skipAuth,
    orgId,
    onUploadProgress,
    ...rest
  } = options
  const url = joinUrl(baseUrl, path, params)

  const send = async () => {
    const { headers, body: finalBody } = buildHeadersAndBody({
      headers: initHeaders,
      body,
      skipAuth,
      orgId,
    })
    if (onUploadProgress) {
      return sendWithXhr(
        url,
        rest.method ?? 'GET',
        headers,
        finalBody,
        rest.signal ?? undefined,
        onUploadProgress,
      )
    }
    return fetch(url, { ...rest, headers, body: finalBody })
  }

  let res = await send()
  let payload = await readPayload<T>(res)
  if (!skipAuth && isUnauthorized(res, payload, bizUnauthorizedCode)) {
    const action = await resolveUnauthorized(false)
    if (action === 'retry') {
      res = await send()
      payload = await readPayload<T>(res)
      if (isUnauthorized(res, payload, bizUnauthorizedCode)) {
        await resolveUnauthorized(true)
      }
    }
  }

  return toBizResult<T>(res, payload)
}

export function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return executeRequest(
    env.apiBaseUrl ?? '',
    path,
    options,
    DEVELOPER_UNAUTHORIZED_CODE,
  )
}

/** 调空间管理后台 org-server（独立基址；401 处理与 developer-api 相同）。 */
export function orgRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return executeRequest(env.orgApiBaseUrl ?? '', path, options)
}
